<script setup lang="ts">
import { inject, Ref, ref, computed } from 'vue';
import cloneDeep from 'lodash/cloneDeep';
import { helpers, required, minValue, maxValue } from '@vuelidate/validators';
import { useVuelidate } from '@vuelidate/core';
import { getCurrentLocale } from '@/i18n';
import { useI18n } from 'vue-i18n';
import { LANG_ENGLISH } from '@/constants';
import { useToast } from 'primevue/usetoast';
import Button from 'primevue/button';
import InputNumber from 'primevue/inputnumber';
import MultiSelect from 'primevue/multiselect';

const currentLanguage = getCurrentLocale();
const { t } = useI18n({ useScope: 'global' });
const dialogRef = inject('dialogRef') as Ref;
const isSave = dialogRef?.value?.options.emits.isSave;
const listTypes = dialogRef?.value?.data?.listGroupTypes;
const isStart = dialogRef?.value?.data?.isStart;
const groupTypeData = dialogRef?.value?.data?.groupTypeData;
const dateData = dialogRef?.value?.data?.dateData;
const isShowError = ref(false);
const toast = useToast();

const cloneGroupTypeData = ref(
  groupTypeData?.length
    ? cloneDeep(groupTypeData)
    : [
        {
          limit: 1,
          groupList: []
        }
      ]
);

const hasEmptyGroupList = (dateData: any) => {
  return dateData.some((item: any) => item.groupList.length);
};

const getMinLimit = computed(() => {
  if (!isStart) {
    return hasEmptyGroupList(dateData) ? Math.max(...dateData.map((item: any) => item.limit)) : 1;
  } else {
    return 1;
  }
});

const getMaxLimit = computed(() => {
  if (isStart) {
    return hasEmptyGroupList(dateData) ? Math.min(...dateData.map((item: any) => item.limit)) : 900;
  } else {
    return 900;
  }
});

const returnMessageValidateMin = computed(() => {
  if (!isStart) {
    return t('validate.end_date_greater_than');
  } else {
    return t('validate.min_value');
  }
});

const returnMessageValidateMax = computed(() => {
  if (isStart) {
    return t('validate.start_date_less_than');
  } else {
    return t('validate.max_value');
  }
});

const rules = {
  $each: helpers.forEach({
    limit: {
      required: helpers.withMessage(t('validate.required'), required),
      minValue: helpers.withMessage(returnMessageValidateMin.value, minValue(getMinLimit.value)),
      maxValue: helpers.withMessage(returnMessageValidateMax.value, maxValue(getMaxLimit.value))
    }
  })
};

const v$ = useVuelidate(rules, cloneGroupTypeData);

const saveConfig = () => {
  if (!isAllSelected.value) {
    toast.add({ severity: 'error', summary: '', detail: t('config_items.select_all_group_types') });
  } else {
    v$.value.$validate();
    isShowError.value = true;
    if (!v$.value.$error) {
      const saveRules = removeEmtyRules(cloneGroupTypeData.value);
      isSave(saveRules);
    }
  }
};

const removeEmtyRules = (arr: Array<any>) => {
  return arr.filter((item: any) => item.groupList.length > 0);
};

const addNewConfig = () => {
  cloneGroupTypeData.value.push({
    limit: 1,
    groupList: []
  });
};

const clearConfig = (clearIndex: number) => {
  cloneGroupTypeData.value = cloneGroupTypeData.value.filter((_: any, index: number) => index !== clearIndex);
};

const getAvailableOptions = (currentIndex: number) => {
  const selectedValues = new Set(cloneGroupTypeData.value.filter((_: any, index: number) => index !== currentIndex).flatMap((item: any) => item.groupList));
  const listTypeReturn = listTypes.filter((option: any) => !selectedValues.has(option.sk));
  return listTypeReturn;
};

const isAllSelected = computed(() => {
  const selectedValues = new Set(cloneGroupTypeData.value?.flatMap((item: any) => item.groupList));
  return listTypes?.every((option: any) => selectedValues.has(option.sk));
});
</script>
<template>
  <div class="calendar-modal">
    <div v-for="(item, quoteIndex) in cloneGroupTypeData" :key="quoteIndex" class="flex mb-3 list-quote-rule">
      <div class="limit-date col-3">
        <div class="header-title mb-2">{{ $t('config_items.day_from_today') }}</div>
        <div>
          <InputNumber v-model="item.limit" class="mr-3 input-limit" :class="isShowError && v$.$each.$response.$errors[quoteIndex]?.limit[0]?.$message ? 'p-invalid' : ''"></InputNumber
          ><span class="text-red-500 validate-txt" v-if="isShowError">
            {{ v$.$each.$response.$errors[quoteIndex]?.limit[0]?.$message }}
          </span>
        </div>
      </div>
      <div class="group-title-select col-9 limit-date">
        <div class="header-title mb-2">{{ $t('config_items.group_type') }}</div>
        <div class="selected-type flex">
          <MultiSelect
            display="chip"
            optionValue="sk"
            :optionLabel="currentLanguage === LANG_ENGLISH ? 'name' : 'nameFr'"
            v-model="item.groupList"
            :options="getAvailableOptions(quoteIndex)"
            :placeholder="$t('advance_quote.form.select_group_type')"
            class="multi-grouptype w-full mr-3"
            :id="`groupTypeSelector_` + quoteIndex"
          />
          <i v-if="cloneGroupTypeData.length > 1" class="pi pi-times-circle text-primary text-lg cursor-pointer align-self-center" @click="clearConfig(quoteIndex)"></i>
        </div>
      </div>
    </div>
    <div class="add_txt text-right" v-if="!isAllSelected" @click="addNewConfig()">+ {{ isStart ? $t('config_items.add_additional_start') : $t('config_items.add_additional_end') }}</div>
    <div class="text-center mt-5">
      <Button id="submitButton" :label="$t('common.buttons.submit')" @click="saveConfig()"></Button>
    </div>
  </div>
</template>
<style lang="scss" scoped>
.add_txt {
  color: $col-red;
  text-decoration: underline;
  cursor: pointer;
}

.limit-date {
  display: flex;
  flex-direction: column;
  .header-title {
    height: 3.25rem;
  }
}
</style>
